import * as React from 'react';

import { Button, cn, Menu, MenuItem } from '@rematter/ui-web';
import { Eye, MoreHorizontal, XCircle } from 'lucide-react';

import { ContainerRecord, enumContainerStatus } from '../../types/container';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { tradeRoutePaths as tradeRoutes } from '~/modules/trade/routes';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

interface ContainerActionMenuProps {
  container: ContainerRecord;
  onVoid?: (containerId: string) => void;
  onRefetch?: () => void;
}

export const ContainerActionMenu: React.FC<ContainerActionMenuProps> = ({
  container,
  onVoid,
}) => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  const handleViewDetails = React.useCallback(() => {
    navigate(tradeRoutes.containerDetail, {
      params: { id: container.id },
      keepSearchParams: false,
    });
  }, [container.id, navigate]);

  const handleVoid = React.useCallback(() => {
    onVoid?.(container.id);
  }, [container.id, onVoid]);

  const canVoid =
    container.status !== enumContainerStatus.Shipped &&
    container.status !== enumContainerStatus.Voided;

  const menuItems = React.useMemo(() => {
    const items: MenuItem[] = [
      {
        label: (
          <div className="flex items-center gap-2">
            <Eye className="size-4" />
            {t.common.viewDetails}
          </div>
        ),
        onAction: handleViewDetails,
      },
    ];

    if (canVoid) {
      items.push('separator');
      items.push({
        label: (
          <div className="flex items-center gap-2">
            <XCircle className="size-4" />
            {t.containersView.actions.void}
          </div>
        ),
        onAction: handleVoid,
      });
    }

    return items;
  }, [t, handleViewDetails, handleVoid, canVoid]);

  return (
    <div className={cn('@ContainerActionMenu flex justify-center')}>
      <Menu
        trigger={
          <Button size="icon" icon={<MoreHorizontal />} variant="outline" />
        }
        items={menuItems}
      />
    </div>
  );
};
